import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';
import CommonSalesQuotationInternationalFields from "./CommonSalesQuotationInternationalFields";

const CreatePumpInternationalQuotation = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        customer: null,
        customerDetails: {
            contactperson: '',
            contactno: '',
            email: '',
            freight: '',
            packingandforwarding: '',
            paymentterm: '',
            incoterms: ''
        },
        customerMultipleAddress: null,
        rows: [],
        exchangeRate: 1,
        exportchargescurrency: 0,
        exportpreparationchargesinr: '',
        selectedCurrency: " "

    });
    const [quotationno, setquotationno] = useState('');
    const [quotationdate, setquotationdate] = useState('');
    const [quotevalidity, setquotevalidity] = useState('');
    const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
    const [isEnquiryValid, setIsEnquiryValid] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);


    const checkquotationno = async (number) => {
        if (!number) {
            setEnquiryValidationMsg('');
            setIsEnquiryValid(false);
            return;
        }

        if (!/^[A-Za-z0-9-]+$/.test(number)) {
            setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
            setIsEnquiryValid(false);
            return;
        }

        try {
            const response = await axios.get(`${BASE_URL}quotation/check/${number}`, {
                headers: authHeader(),
            });

            if (response.data === true) {
                setEnquiryValidationMsg('❌ This enquiry number already exists.');
                setIsEnquiryValid(false);
            } else if (response.data === false) {
                setEnquiryValidationMsg('✅ This enquiry number is available.');
                setIsEnquiryValid(true);
            } else {
                setEnquiryValidationMsg('Unexpected response from server.');
                setIsEnquiryValid(false);
            }
        } catch (error) {
            console.error("Error checking enquiry number:", error);
            setEnquiryValidationMsg('❌ Error validating enquiry number.');
            setIsEnquiryValid(false);
        }
    };


    useEffect(() => {
        const debouncedCheck = debounce(() => {
            checkquotationno(quotationno);
        }, 400);

        debouncedCheck();

        return () => debouncedCheck.cancel();
    }, [quotationno]);


    // Handle data changes from child component
    const handleDataChange = (newData) => {
        setFormData(prev => ({
            ...prev,
            ...newData,
            rows: newData.rows || prev.rows
        }));
    };

    // Submit new quotation
    const handleSubmit = async () => {
        setIsLoading(true);
        setFormSubmitted(true);
        
        if (!isEnquiryValid) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid enquiry number before submitting.',
            });
            setIsLoading(false);
            return;
        }

        const {
            customer,
            customerDetails,
            customerMultipleAddress,
            rows,
            exchangeRate,
            exportchargescurrency,
            exportpreparationchargesinr,
            selectedCurrency
        } = formData;

        // ✅ FIXED: Added proper null checks
        // Validation
        if (!customer?.value) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please select a customer',
            });
            setIsLoading(false);
            return;
        }

        if (!rows || rows.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please add at least one product',
            });
            setIsLoading(false);
            return;
        }

        // ✅ FIXED: Added safety checks for calculations
        // Calculate totals
        const misccharges = (exportchargescurrency || 0) * (exchangeRate || 1);
        const netTotalInr = (rows || []).reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0);
        const netTotalCurrency = (rows || []).reduce((total, row) => total + (parseFloat(row.totalcurrency) || 0), 0);
        const grandTotalInr = netTotalInr + misccharges;
        const grandTotalCurrency = netTotalCurrency + (exportchargescurrency || 0);

        // ✅ FIXED: Added safety checks for row data
        // Prepare quotation items
        const quotationItems = (rows || []).map(row => ({
            product: { id: row.value || '' },
            productremarks: row.productremarks || '',
            quantity: parseFloat(row.quantity) || 0,
            unitrateinr: parseFloat(row.unitRateInr) || 0,
            totalinr: parseFloat(row.totalInr) || 0,
            unitratecurrency: parseFloat(row.unitratecurrency) || 0,
            totalcurrency: parseFloat(row.totalcurrency) || 0,
            exchangerate: parseFloat(row.exchangerate) || exchangeRate || 1,
            itemcode: row.itemcode || '',
            hsnsacno: row.hsnsacno || '',
            serialnumber: row.serialnumber || '',
            modelnumber: row.modelnumber || '',
            tagnumber: row.tagnumber || '',
            moc: row.moc || '',
            partno: row.partno || '',
            uom: row.uom || '',
            currency: selectedCurrency || '',
        }));

        // ✅ FIXED: Added proper null checks
        // Prepare main quotation data
        const quotationData = {
            quotationtype: 'Pump Spare',
            quotationcategory: 'International',
            quotationno,
            quotationdate,
            quotevalidity,
            quotationItems,
            customer: { id: customer.value },
            customeraddress: customerMultipleAddress?.value ? { id: customerMultipleAddress.value } : null,
            contactperson: customerDetails?.contactperson || '',
            contactno: customerDetails?.contactno || '',
            email: customerDetails?.email || '',
            freight: customerDetails?.freight || '',
            packingandforwarding: customerDetails?.packingandforwarding || '',
            paymentterm: customerDetails?.paymentterm || '',
            incoterms: customerDetails?.incoterms || '',
            quotationcurrency: selectedCurrency || '',
            quotationexchangerate: exchangeRate || 1,
            grandtotalinr: grandTotalInr,
            grandtotalcurrency: grandTotalCurrency,
            exportpreparationchargescurrency: exportchargescurrency || 0,
            exportpreparationchargesinr: exportpreparationchargesinr || '',
            nettotalinr: netTotalInr,
            nettotalcurrency: netTotalCurrency,
        };

        try {
            await axios.post(`${BASE_URL}quotation/save`, quotationData, {
                headers: authHeader()
            });

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Quotation created successfully!',
            });
            navigate("/quotationIndexTable/1");
        } catch (error) {
            console.error("Error creating quotation:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to create quotation. Please try again.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container">
            <br />
            <Form>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Quotation Number: *</Form.Label>
                            <Form.Control
                                type="text"
                                value={quotationno}
                                onChange={e => {
                                    setquotationno(e.target.value);
                                    setFormSubmitted(false);
                                }}
                                isInvalid={
                                    (formSubmitted && !isEnquiryValid) ||
                                    enquiryValidationMsg.includes('exists') ||
                                    enquiryValidationMsg.includes('Error') ||
                                    enquiryValidationMsg.includes('allowed')
                                }
                                required
                            />
                            {enquiryValidationMsg && (
                                <Form.Control.Feedback type={
                                    enquiryValidationMsg.includes('exists') ||
                                        enquiryValidationMsg.includes('Error') ||
                                        enquiryValidationMsg.includes('allowed') ?
                                        "invalid" : "valid"
                                }>
                                    {enquiryValidationMsg}
                                </Form.Control.Feedback>
                            )}
                            {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter a valid enquiry number
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Quotation Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={quotationdate}
                                onChange={(e) => setquotationdate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Quote validity</Form.Label>
                            <Form.Control
                                type="text"
                                value={quotevalidity}
                                onChange={(e) => setquotevalidity(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* ✅ FIXED: Added error boundary for the child component */}
                <CommonSalesQuotationInternationalFields
                    onDataChange={handleDataChange}
                    initialData={formData}
                />

                <br />
                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-primary me-2"
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" />
                                Creating...
                            </>
                        ) : 'CREATE QUOTATION'}
                    </button>

                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => navigate("/pindex/1")}
                        disabled={isLoading}
                    >
                        CANCEL
                    </button>
                </div>
            </Form>
        </div>
    );
};

export default CreatePumpInternationalQuotation;